import { useEffect, useState } from 'react';
import { authHeaders } from '@/lib/shell';
import { useSessionBoundFetch } from '@/lib/use-session-guard';

// Cached pictures need the session's expected company/person headers on web
// and native alike. The guarded fetch supplies them; signed pictures retain
// their separate direct route.

const MAX_PICTURE_BYTES = 1024 * 1024;

type Fetched = { source: string; url: string | null };

/** Cached pixels use the captured session; refused/loading pixels are a
 * placeholder. Signed pictures keep their original direct source. */
export function useSessionPicture(src: string | null): { src: string | null; fetched: boolean } {
  const fetchPicture = useSessionBoundFetch();
  const fetched = Boolean(src?.includes('/cached-picture/'));
  const [picture, setPicture] = useState<Fetched | null>(null);
  useEffect(() => {
    if (!fetched || !src) return;
    const abort = new AbortController();
    let url: string | null = null;
    void (async () => {
      try {
        const response = await fetchPicture(src, {
          credentials: 'same-origin',
          headers: authHeaders(),
          signal: abort.signal,
        });
        const declared = Number(response.headers.get('content-length') ?? 0);
        if (
          !response.ok ||
          response.headers.get('content-type') !== 'image/webp' ||
          declared > MAX_PICTURE_BYTES
        )
          throw new Error('app_picture_unavailable');
        const blob = await response.blob();
        if (abort.signal.aborted) return;
        if (blob.size === 0 || blob.size > MAX_PICTURE_BYTES)
          throw new Error('app_picture_unavailable');
        url = URL.createObjectURL(new Blob([blob], { type: 'image/webp' }));
        setPicture({ source: src, url });
      } catch {
        // A refused, changed or oversized picture is the placeholder, never an older picture.
        if (!abort.signal.aborted) setPicture({ source: src, url: null });
      }
    })();
    return () => {
      abort.abort();
      if (!url) return;
      const released = url;
      URL.revokeObjectURL(released);
      // A released URL never answers again, even if the same picture comes back before it is read anew.
      setPicture((current) => (current?.url === released ? null : current));
    };
  }, [src, fetched, fetchPicture]);
  if (!fetched) return { src, fetched: false };
  return { src: picture?.source === src ? picture.url : null, fetched: true };
}
